/* nav-ia-005 — V2 nav model (the Claude Design role-aware sidebar).
   ════════════════════════════════════════════════════════════════
   This AUGMENTS the shipped nav config (navConfig.js) with the design's
   group metadata (kind / default-open / group icon) and a distinct
   per-item icon map. It deliberately reuses navConfig's `visibleNavItems`
   (role + module gating) and `groupVisibleItems`, so V2 has the SAME
   role/label/route visibility as the live sidebar — only the presentation
   is new. Group set updated for the 2026-07-20 Command Center reorg
   (STARSEED-CAPMARKETS-052).
   ════════════════════════════════════════════════════════════════ */

import {
  LayoutDashboard, Gauge, BadgeCheck, ReceiptText, Briefcase,
  Network, UserRound, Mail, CreditCard, Files, Compass, UsersRound,
  Gem, Share2, Settings, UserCog, UserPlus,
  History, Filter, Import, Radar, ClipboardList, FolderLock,
  Ticket, Hash, Inbox, Wallet, PlugZap, BellRing, ScanFace, ShieldAlert,
} from "lucide-react";
import { NAV_GROUPS, visibleNavItems, groupVisibleItems } from "./navConfig";

// Per-group presentation metadata (keyed by navConfig group key). `kind`:
// 'flat' renders items at top level (no header); 'group' is a collapsible
// section.
const GROUP_META = {
  overview:             { kind: "flat",  defaultOpen: true,  icon: null,        area: "member" },
  account:              { kind: "group", defaultOpen: true,  icon: UserRound,   area: "member" },
  get_started:          { kind: "group", defaultOpen: true,  icon: Compass,     area: "member" },
  invest:               { kind: "group", defaultOpen: true,  icon: Gem,         area: "member" },
  earn:                 { kind: "group", defaultOpen: true,  icon: Share2,      area: "member" },
  admin_command_center: { kind: "group", defaultOpen: true,  icon: Radar,       area: "admin" },
  admin_members:        { kind: "group", defaultOpen: true,  icon: UsersRound,  area: "admin" },
  admin_settings:       { kind: "group", defaultOpen: false, icon: Settings,    area: "admin" },
};

// Distinct lucide glyph per destination (resolves the old shield ×6 / mail ×3
// collisions). Keyed by route. Falls back to the item's shipped icon if absent.
const ITEM_ICON = {
  "/dashboard": LayoutDashboard,
  "/payments": CreditCard,
  "/messages": Mail,
  "/kyc": Files,
  "/command-center": Gauge,
  "/kyc-verification": BadgeCheck,
  "/partner-funding": ReceiptText,
  "/dealflow": Briefcase,
  "/introducer-program": Network,
  "/admin/command-center": Radar,
  "/admin/investor-files": FolderLock,
  "/compliance-pipeline": ClipboardList,
  "/lead-management": Filter,
  "/import-contacts": Import,
  "/admin/vc-registry": Hash,
  "/admin/promo-codes": Ticket,
  "/funding": Inbox,
  "/membership-fees": Wallet,
  "/admin/stripe/preflight": PlugZap,
  "/announcements": BellRing,
  "/verification": ScanFace,
  "/admin/security-status": ShieldAlert,
  "/users": UserCog,
  "/admin/users/invite": UserPlus,
  "/admin/activity-log": History,
  "/settings": Settings,
};

const GROUP_ORDER = NAV_GROUPS.map((g) => g.key);

/* Build the ordered, non-empty V2 group model for a user. Same visibility as
   the live sidebar (visibleNavItems), enriched with V2 presentation metadata. */
export function buildNavModelV2(user) {
  const visible = visibleNavItems(user);
  return groupVisibleItems(visible).map((g) => {
    const meta = GROUP_META[g.key] || { kind: "group", defaultOpen: true, icon: null, area: "member" };
    return {
      key: g.key,
      label: g.label,
      kind: meta.kind,
      defaultOpen: meta.defaultOpen,
      icon: meta.icon,
      area: meta.area,
      items: g.items.map((it) => ({ ...it, iconV2: ITEM_ICON[it.href] || it.icon })),
    };
  });
}

// Index of the first admin-area group (for the "Admin" divider). -1 if none.
export function firstAdminIndex(groups) {
  return groups.findIndex((g) => g.area === "admin");
}

export { GROUP_ORDER };
